import {
  normalizeRoutingRuleset,
  normalizeSingleRoutingRuleset,
} from 'api/routingRuleset/normalizers';
import { nullableMetadata } from 'components/Metadata/constants';
import {
  normalizeBinForPayload,
  normalizeMetadataPayload,
} from 'routes/pages/AuthorizedStack/TrafficManagement/RoutingRuleset/Ruleset/Form/utils/helpers';
import {
  ICustomer,
  ITransaction,
} from 'routes/pages/AuthorizedStack/TrafficManagement/RoutingRuleset/Ruleset/Form/utils/types';
import { prepareFormData } from 'utils/form';
import { INullable, TObject } from 'utils/types';

import {
  IBulkCopyChangeRulesResult,
  IBulkCreateChangeRulesResult,
  IChangeRule,
  IChangeRuleCpm,
  IChangeRuleData,
  IChangeRuleForm,
  ISingleChangeRule,
  ISingleChangeRuleData,
} from './types';

const normalizeCpm = (data?: TObject): INullable<IChangeRuleCpm> => {
  if (!data) {
    return null;
  }
  return {
    name: (data?.name as string) || '',
    paymentMethod: (data?.paymentMethod as string) || '',
  };
};

export const normalizeChangeRule = (data: IChangeRuleData): IChangeRule => ({
  ...normalizeRoutingRuleset(data),
  fromCpm: normalizeCpm(data?.fromCpm as TObject),
  toCpm: normalizeCpm(data?.toCpm as TObject),
});

export const normalizeSingleChangeRule = (
  data: ISingleChangeRuleData,
): ISingleChangeRule => ({
  ...normalizeSingleRoutingRuleset(data),
  fromCpm: normalizeCpm(data?.fromCpm as TObject),
  toCpm: normalizeCpm(data?.toCpm as TObject),
});

const omitCustomerPhonePrefix = (
  customer: INullable<ICustomer>,
): IChangeRuleForm['customer'] => {
  if (!customer) {
    return null;
  }
  const { phonePrefix: _removed, ...rest } = customer;
  return rest;
};

export const mapSingleChangeRuleToForm = (data: ISingleChangeRule): IChangeRuleForm => ({
  bin: data.bin,
  customer: omitCustomerPhonePrefix(data.customer),
  enabled: data.enabled,
  fromCpmCode: data.fromCpm?.paymentMethod || null,
  merchantTerminalId: data.merchantTerminal.id,
  name: data.name,
  priority: data.priority,
  stats: data.stats,
  toCpmCode: data.toCpm?.paymentMethod || null,
  transaction: {
    amount: data.transaction.amount,
    metadata: data.transaction.metadata?.length
      ? data.transaction.metadata.map((row) => ({ ...row, useTemplate: false }))
      : [nullableMetadata],
    txAmount: data.transaction.txAmount,
    txCcy: data.transaction.txCcy,
    type: data.transaction.type,
  },
});

export const normalizeBulkCopyChangeRulesResult = (
  data?: TObject,
): IBulkCopyChangeRulesResult => ({
  copiedChangeRuleCount: (data?.copiedChangeRuleCount as number) || 0,
  copiedChangeRuleIdsByMerchantTerminal:
    (data?.copiedChangeRuleIdsByMerchantTerminal as Record<string, number[]>) || {},
  targetMerchantTerminalCount: (data?.targetMerchantTerminalCount as number) || 0,
});

export const normalizeBulkCreateResult = (
  data?: TObject,
): IBulkCreateChangeRulesResult => ({
  createdChangeRuleCount: (data?.createdChangeRuleCount as number) || 0,
});

export const normalizeCpmNames = (data: unknown): string[] => {
  const names = Array.isArray(data) ? data : [];

  return names
    .filter((name): name is string => typeof name === 'string' && !!name)
    .sort((first, second) => first.localeCompare(second));
};

// Generic so the single-rule and bulk payloads share one cleanup of the filter blocks.
export const normalizeChangeRulePayload = <
  T extends Pick<IChangeRuleForm, 'bin' | 'customer' | 'transaction'>,
>(
  payload: T,
) => {
  const { excludedFromCascading: _removed, ...transaction } =
    payload.transaction as ITransaction;

  return prepareFormData({
    ...payload,
    bin: normalizeBinForPayload(payload.bin),
    customer: omitCustomerPhonePrefix(payload.customer as INullable<ICustomer>),
    transaction: {
      ...transaction,
      metadata: normalizeMetadataPayload(transaction.metadata),
    },
  });
};
